/**
 * ContactSection — phone + email capture for PersonDialog.
 *
 * Minimal section: 3 phone inputs (home / cell / business) and 2 email fields
 * (primary + optional secondary). Phones store digits only (14-char max) to
 * match Core Director's field-length rules for PhoneHome / PhoneCell /
 * PhoneBus. Formatting for display (e.g. `573-555-1234`) is applied at
 * presentation time — not to the stored value.
 *
 * Uses the same `isChanged(field)` callback pattern as AddressSection to
 * drive the "what the officer touched" blue-accent style.
 */
import { useCallback, type CSSProperties } from 'react';
import type { RaoPerson } from '../../../types';

// -----------------------------------------------------------------------------
// Public props
// -----------------------------------------------------------------------------

export interface ContactSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  isChanged?: (field: string) => boolean;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Styles (mirrored from AddressSection for visual consistency)
// -----------------------------------------------------------------------------

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--ink-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontWeight: 600,
  marginBottom: 3,
};

const inputStyleBase: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--ink-200)',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};

const inputStyleChanged: CSSProperties = {
  ...inputStyleBase,
  borderLeft: '3px solid var(--pcb-blue)',
  background: 'var(--field-changed-bg, #f0f7ff)',
  color: 'var(--ink-900, #1d1d1f)',
};

const readOnlyInputStyle: CSSProperties = {
  ...inputStyleBase,
  background: 'var(--ink-50, #f5f5f5)',
  color: 'var(--ink-700)',
};

const sectionHeaderStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: 'var(--pcb-blue)',
  marginBottom: 8,
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ContactSection({
  person,
  onChange,
  isChanged,
  readOnly = false,
}: ContactSectionProps): JSX.Element {
  const styleFor = useCallback(
    (field: string): CSSProperties => {
      if (readOnly) return readOnlyInputStyle;
      if (isChanged && isChanged(field)) return inputStyleChanged;
      return inputStyleBase;
    },
    [isChanged, readOnly],
  );

  // Phones — strip non-digits on change, cap at 14 chars (future-proofs
  // international numbers; Core Director truncates to its 10-char field).
  const handlePhoneChange = useCallback(
    (field: 'phoneHome' | 'phoneCell' | 'phoneBus', raw: string): void => {
      const digits = raw.replace(/\D/g, '').slice(0, 14);
      onChange({ [field]: digits } as Partial<RaoPerson>);
    },
    [onChange],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={sectionHeaderStyle}>Contact</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {/* Cell first, then home, then business (BACKLOG-1778, Bill 2026-10-16) - the same order
            as the sole-proprietor dialog. Field keys and the cell -> home -> business fallback
            used elsewhere are unchanged. */}
        <div>
          <label style={labelStyle} htmlFor="rao-contact-cell">Cell phone</label>
          <input
            id="rao-contact-cell"
            type="tel"
            value={person.phoneCell ?? ''}
            onChange={(e) => handlePhoneChange('phoneCell', e.currentTarget.value)}
            readOnly={readOnly}
            style={styleFor('phoneCell')}
            maxLength={14}
            placeholder="5735551234"
            title="Cell phone"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-contact-home">Home phone</label>
          <input
            id="rao-contact-home"
            type="tel"
            value={person.phoneHome ?? ''}
            onChange={(e) => handlePhoneChange('phoneHome', e.currentTarget.value)}
            readOnly={readOnly}
            style={styleFor('phoneHome')}
            maxLength={14}
            placeholder="5735551234"
            title="Home phone"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-contact-bus">Business phone</label>
          <input
            id="rao-contact-bus"
            type="tel"
            value={person.phoneBus ?? ''}
            onChange={(e) => handlePhoneChange('phoneBus', e.currentTarget.value)}
            readOnly={readOnly}
            style={styleFor('phoneBus')}
            maxLength={14}
            placeholder="5735551234"
            title="Business phone"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-contact-email">Email</label>
          <input
            id="rao-contact-email"
            type="email"
            value={person.email ?? ''}
            onChange={(e) => onChange({ email: e.currentTarget.value })}
            readOnly={readOnly}
            style={styleFor('email')}
            placeholder="name@example.com"
            title="Primary email"
          />
        </div>
        {/* Email 2 removed (Bill 2026-10-17 smoke test): it had no Core Director destination, so a change to it was
            reported as saved while nothing reached Core Director. */}
      </div>
    </div>
  );
}
